import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { House, ShoppingCart, Receipt, User, SignOut } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/lib/store";
import { TID } from "@/lib/testIds";
import { Toaster } from "@/components/ui/sonner";
import AgeVerificationModal from "@/components/AgeVerificationModal";

const NavItem = ({ to, icon: Icon, label, testId, badge, colour = "var(--red)" }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    className={({ isActive }) =>
      `relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 transition ${
        isActive
          ? "text-white border-[var(--text)]"
          : "text-[var(--text)] border-transparent hover:border-[var(--text)]"
      }`
    }
    style={({ isActive }) => (isActive ? { background: colour, boxShadow: "3px 3px 0 0 var(--text)" } : {})}
  >
    <Icon size={18} weight="fill" />
    <span className="hidden sm:inline">{label}</span>
    {typeof badge === "number" && badge > 0 && (
      <span
        data-testid={TID.cartBadge}
        className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-[var(--yellow)] text-[11px] font-black text-[var(--text)] border-2 border-[var(--text)]"
      >
        {badge}
      </span>
    )}
  </NavLink>
);

export default function CustomerLayout({ children }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const cartCount = useCart((s) => s.count());

  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--text)]">
      <div className="w-full bg-[var(--red)] text-white border-b-[3px] border-[var(--text)] py-1.5 text-center text-[11px] font-bold tracking-widest uppercase">
        18+ only · Cape Town&#39;s brightest cannabis corner shop · Free delivery over R500
      </div>
      <header className="sticky top-0 z-40 bg-[var(--cream)] border-b-[3px] border-[var(--text)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 mr-4">
            <div
              className="w-10 h-10 rounded-full bg-[var(--red)] border-2 border-[var(--text)] flex items-center justify-center"
              style={{ boxShadow: "3px 3px 0 0 var(--text)" }}
            >
              <span className="font-display text-white text-lg">T</span>
            </div>
            <span className="font-display text-xl hidden sm:inline">Tjoef</span>
          </button>
          <nav className="flex items-center gap-2 flex-1 flex-wrap">
            <NavItem to="/" icon={House} label="Shop" testId={TID.navHome} colour="var(--red)" />
            <NavItem to="/orders" icon={Receipt} label="Orders" testId={TID.navOrders} colour="var(--blue)" />
          </nav>
          <NavItem
            to="/cart"
            icon={ShoppingCart}
            label="Basket"
            testId={TID.navCart}
            badge={cartCount}
            colour="var(--green)"
          />
          <NavItem to="/profile" icon={User} label="Profile" testId={TID.navProfile} colour="var(--pink)" />
          <button
            data-testid={TID.navLogout}
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            title="Sign out"
            className="ml-1 w-9 h-9 rounded-full border-2 border-[var(--text)] bg-white text-[var(--text)] hover:bg-[var(--yellow)] transition flex items-center justify-center"
            style={{ boxShadow: "3px 3px 0 0 var(--text)" }}
          >
            <SignOut size={16} weight="bold" />
          </button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 pb-20">{children}</main>
      <AgeVerificationModal />
      <Toaster theme="light" position="top-center" richColors />
    </div>
  );
}
